import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Drawer,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import DashboardLayout from '../../../../../../components/layout/DashboardLayout';
import DataTable from '../../../../../../components/ui/DataTable';
import { LoadingPage } from '../../../../../../components/ui/LoadingSpinner';
import { formatDate, formatAnalyticsCount } from '../../../../../../lib/utils';
import apiClient from '../../../../../../lib/api';
import { normalizeListPagination } from '../../../../../../lib/normalizeListPagination';
import { toast } from 'react-hot-toast';
import GooglePlacesAutocomplete from '../../../../../../components/GooglePlacesAutocomplete';

const BranchObituariesPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  // Handle nested route: /companies/:companyId/branches/:branchId/obituaries
  // React Router will provide companyId and branchId from the route params
  const companyId = params.companyId;
  const branchId = params.branchId;
  const [obituaries, setObituaries] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [loading, setLoading] = useState(true);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [branch, setBranch] = useState(null);
  const [company, setCompany] = useState(null);

  // Load branch and company data
  const fetchBranch = async () => {
    try {
      const response = await apiClient.getBranch(branchId);
      const branchData = response.branch || response.data || response;
      setBranch(branchData);
      
      // Fetch company if we have companyId
      if (companyId) {
        const companyResponse = await apiClient.getFuneralCompany(companyId);
        const companyData = companyResponse.data || companyResponse;
        setCompany(companyData);
      }
    } catch (error) {
      console.error('Error fetching branch/company:', error);
      toast.error('Failed to load branch information');
    }
  };

  // Load obituaries data filtered by branch
  const loadObituaries = async (searchParams) => {
    try {
      setLoading(true);
      
      const params = {
        page: (searchParams?.page ?? 1),
        limit: (searchParams?.rowsPerPage ?? 10),
        branchId: branchId, // Filter by branch ID
      };
      
      // Add search term if provided
      if (searchParams?.searchTerm) {
        params.search = searchParams.searchTerm;
      }
      
      
      console.log('Loading obituaries with params:', params);
      
      const response = await apiClient.getObituaries(params);
      console.log('Response:', response);
      
      if (response.data) {
        setObituaries(response.data);
        setPagination(normalizeListPagination(response, { limit: 10 }));
      } else {
        setObituaries([]);
        setPagination({
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        });
      }
    } catch (error) {
      console.error('Error loading obituaries:', error);
      toast.error('Failed to load obituaries');
      setObituaries([]);
      setPagination({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (branchId) {
      fetchBranch();
      loadObituaries();
    }
  }, [branchId, companyId]);

  const columns = [
    {
      id: 'deceasedName',
      label: 'Deceased Name',
      sortable: true,
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {value || 'Unknown'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'dateOfDeath',
      label: 'Date of Death',
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2" color="text.secondary">
          {value ? formatDate(value) : 'Unknown'}
        </Typography>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2" color="text.secondary">
          {value ? formatDate(value) : 'Unknown'}
        </Typography>
      ),
    },
    {
      id: 'viewed',
      label: 'Views',
      sortable: true,
      render: (_, row) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatAnalyticsCount(row.viewed)}
        </Typography>
      ),
    },
    {
      id: 'storeVisits',
      label: 'Store',
      sortable: true,
      render: (_, row) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatAnalyticsCount(row.storeVisits)}
        </Typography>
      ),
    },
    {
      id: 'cardsSold',
      label: 'Cards',
      sortable: true,
      render: (_, row) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatAnalyticsCount(row.cardsSold)}
        </Typography>
      ),
    },
  ];

  const filters = [
    {
      key: 'search',
      label: 'Search',
      type: 'text',
      placeholder: 'Search by name or title...'
    },
  ];

  // Handle obituary actions
  const handleRowSelect = (obituary) => {
    // Navigate to nested obituary detail route
    navigate(`/companies/${companyId}/branches/${branchId}/obituaries/${obituary.id}`);
  };

  const handleSearch = (searchParams) => {
    console.log('Search params:', searchParams);
    loadObituaries(searchParams);
  };

  const handleAddObituary = () => {
    setFormData({
      deceasedName: '',
      dateOfDeath: '',
      branchId: branchId, // Include branch ID when creating
      companyId: companyId, // Include company ID from route
    });
    setCreateDrawerOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Include branchId and companyId when creating obituary
      const response = await apiClient.createObituary({
        ...formData,
        branchId: branchId,
        companyId: companyId, // Include company ID from route
      });
      toast.success('Obituary created successfully');
      setCreateDrawerOpen(false);
      // Navigate to the detail page
      if (response.success && response.data && response.data.id) {
        navigate(`/companies/${companyId}/branches/${branchId}/obituaries/${response.data.id}`);
      } else {
        loadObituaries();
      }
    } catch (error) {
      console.error('Error creating obituary:', error);
      toast.error('Failed to create obituary');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !branch) {
    return <LoadingPage />;
  }

  return (
    <DashboardLayout 
      title={`Obituaries - ${branch?.branchName || 'Branch'}`}
      breadcrumbData={{ 
        companyName: company?.companyName || 'Company',
        branchName: branch?.branchName || 'Branch'
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1">
            Obituaries - {branch?.branchName || 'Branch'}
          </Typography>
          {company && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {company.companyName}
            </Typography>
          )}
        </Box>

        {/* Stats Cards */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2
          }}>
            <Card sx={{
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4a8f 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DescriptionIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Obituaries
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {pagination.total}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{
              background: 'linear-gradient(135deg, #d885e8 0%, #e04a5f 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Published
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {obituaries.filter(o => o.status === 'published').length}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{
              background: 'linear-gradient(135deg, #4a9ee8 0%, #00d8e0 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EditIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Draft
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {obituaries.filter(o => o.status === 'draft' || !o.status).length}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{
              background: 'linear-gradient(135deg, #3dd06f 0%, #32e0c0 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    This Month
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {obituaries.filter(o => {
                    const created = new Date(o.createdAt);
                    const now = new Date();
                    return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
                  }).length}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Obituaries Table */}
        <Box sx={{ mt: 4 }}>
          <DataTable
            data={obituaries}
            title="Obituaries"
            onAdd={handleAddObituary}
            addButtonText="Create Obituary"
            columns={columns}
            filters={filters}
            onSelect={handleRowSelect}
            onSearch={handleSearch}
            totalPages={pagination.totalPages}
            currentPage={pagination.page}
            loading={loading}
            onRowClick={(row) => navigate(`/companies/${companyId}/branches/${branchId}/obituaries/${row.id}`)}
          />
        </Box>

        {/* Create Drawer */}
        <Drawer anchor="right" open={createDrawerOpen} onClose={() => setCreateDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, p: 3, backgroundColor: 'background.paper' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Create Obituary</Typography>
            <IconButton onClick={() => setCreateDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Display branch info when in branch context */}
            {branch && (
              <Box sx={{ p: 2, backgroundColor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Branch
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {branch.branchName || 'Unnamed Branch'}
                </Typography>
              </Box>
            )}
            <TextField fullWidth label="Deceased Name" value={formData.deceasedName} onChange={(e) => setFormData({ ...formData, deceasedName: e.target.value })} required />
            <TextField fullWidth label="Date of Death" type="date" value={formData.dateOfDeath} onChange={(e) => setFormData({ ...formData, dateOfDeath: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Box>

          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => setCreateDrawerOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>{saving ? 'Creating...' : 'Create'}</Button>
          </Box>
        </Drawer>
      </Box>
    </DashboardLayout>
  );
};

export default BranchObituariesPage;

